import * as React from "react";
import {
    Typography,
    Box,
    IconButton,
    Button,
    Grid,
    Collapse,
    InputAdornment,
    MenuItem,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { connect } from "react-redux";
import {
    useSearchParams,
} from "react-router-dom";
import {
    centered_flex_box,
    main_button,
    MainInput,
} from "../app/components/Styles";
import { Image, Space, Table } from "antd";
import { cancelOutboundItem, downloadInboundReceipt, exportInbounds, getInbounds, getWarehouses } from "../app/store/actions/dataActions";
import { useEffect } from "react";
import { Search } from "@mui/icons-material";
import moment from "moment";
import NewCard from "../app/components/NewCard";
import DateRangePicker from "../app/components/DateRangePicker";
import { useTranslation } from "react-i18next";
import CreateTicketModal from "../app/components/CreateTicketModal";
import { StatusPill } from "../app/glass/styles";
import SearchFilterBar, { FilterChips, exportIcon } from "../app/glass/SearchFilterBar";
import { patchSearchParams } from "../app/utils/searchParams";

const RETURNS_WAREHOUSE_STATUSES = ["Received", "Partially Received"];

const isReturnsWarehouseEntry = (warehouse) => {
    if (!warehouse) return false;
    if (warehouse.isReturns || warehouse.isReturnsWarehouse || warehouse.returnsWarehouse) return true;
    if (warehouse.type && String(warehouse.type).toLowerCase().includes("return")) return true;
    const label = `${warehouse.name || ""} ${warehouse.label || ""}`;
    return /return|مرتجع/i.test(label);
};

export const Inbounds = ({
    getInbounds,
    getWarehouses,
    cancelOutboundItem,
    exportInbounds,
    downloadInboundReceipt,
    user,
    inbounds,
    warehouses,
    isLoading,
    isWaiting,
    token
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { t } = useTranslation();
    const [error, setError] = React.useState(null);
    const [terms, setTerms] = React.useState(false);
    const [page, setPage] = React.useState(parseInt(searchParams.get('page')) || 1);
    const [pageSize, setPageSize] = React.useState(parseInt(searchParams.get('pageSize')) || 10);
    const [startDate, setStartDate] = React.useState(searchParams.get('startDate') ? moment(searchParams.get('startDate')) : null);
    const [endDate, setEndDate] = React.useState(searchParams.get('endDate') ? moment(searchParams.get('endDate')) : null);
    const [status, setStatus] = React.useState(searchParams.get('status') || '');
    const [targetWarehouse, setTargetWarehouse] = React.useState(searchParams.get('targetWarehouse') || '');
    const [showFilters, setShowFilters] = React.useState(false);
    const [search, setSearch] = React.useState(searchParams.get('search') || '');
    const [warehouseTab, setWarehouseTab] = React.useState(0);
    const [helpTicketOpen, setHelpTicketOpen] = React.useState(false);
    const [helpTicketContext, setHelpTicketContext] = React.useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    /** Keep Inventory hub `tab=history` when updating list filters. */
    const setListParams = React.useCallback((updates) => {
        setSearchParams(patchSearchParams(searchParams, { tab: 'history', ...updates }), { replace: true });
    }, [searchParams, setSearchParams]);

    const statusLabels = React.useMemo(() => ({
        Pending: t("inbounds.status.pending"),
        Receiving: t("inbounds.status.receiving"),
        "Partially Received": t("inbounds.status.partiallyReceived"),
        Received: t("inbounds.status.received"),
        Cancelled: t("inbounds.status.cancelled"),
    }), [t]);

    useEffect(() => {
        getWarehouses({ page: 1, limit: 999999 });
    }, []);

    // Build warehouse tab list (only fetched warehouses)
    const warehouseTabList = React.useMemo(() => {
        if (!warehouses?.docs) return [];
        return [
            ...warehouses.docs.map((w) => ({
                label: w.name,
                value: w._id,
                isReturns: isReturnsWarehouseEntry(w),
            })),
            { label: t("inbounds.outOfChainz"), value: "Out of Chainz", isReturns: false },
        ];
    }, [warehouses, t]);

    const selectedWarehouseTab = warehouseTabList.find((w) => w.value === targetWarehouse);
    const isReturnsWarehouseSelected = Boolean(selectedWarehouseTab?.isReturns);

    const statusFilterOptions = React.useMemo(() => {
        if (isReturnsWarehouseSelected) {
            return RETURNS_WAREHOUSE_STATUSES;
        }
        return ["Pending", "Receiving", "Partially Received", "Received", "Cancelled"];
    }, [isReturnsWarehouseSelected]);

    // Returns warehouse always sends a server-side status filter (Received or Partially Received)
    const effectiveStatus = isReturnsWarehouseSelected
        ? (RETURNS_WAREHOUSE_STATUSES.includes(status) ? status : "Received")
        : status;

    // Set warehouse tab index from targetWarehouse
    useEffect(() => {
        if (!warehouseTabList.length) return;
        // If no warehouse selected, default to first
        if (!targetWarehouse) {
            setTargetWarehouse(warehouseTabList[0].value);
            setWarehouseTab(0);
            return;
        }
        const idx = warehouseTabList.findIndex((w) => w.value === targetWarehouse);
        setWarehouseTab(idx === -1 ? 0 : idx);
    }, [targetWarehouse, warehouseTabList.length]);

    // Force an allowed status when on the returns warehouse tab
    useEffect(() => {
        if (!isReturnsWarehouseSelected) return;
        if (!RETURNS_WAREHOUSE_STATUSES.includes(status)) {
            setStatus("Received");
            setListParams({
                page: "1",
                pageSize: pageSize.toString(),
                startDate: startDate ? startDate.format("YYYY-MM-DD") : null,
                endDate: endDate ? endDate.format("YYYY-MM-DD") : null,
                status: "Received",
                targetWarehouse,
            });
            setPage(1);
        }
    }, [isReturnsWarehouseSelected, status]);

    // When tab changes, update warehouse filter
    const handleWarehouseTabChange = (event, newValue) => {
        setWarehouseTab(newValue);
        const nextTab = warehouseTabList[newValue];
        const warehouseValue = nextTab?.value || "";
        const nextStatus = nextTab?.isReturns
            ? (RETURNS_WAREHOUSE_STATUSES.includes(status) ? status : "Received")
            : status;
        setPage(1);
        setTargetWarehouse(warehouseValue);
        setStatus(nextStatus);
        setListParams({
            page: '1',
            pageSize: pageSize.toString(),
            startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
            endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
            status: nextStatus,
            targetWarehouse: warehouseValue
        });
    };

    // Remove client-side search filtering. Add search to backend params.
    useEffect(() => {
        if ((startDate && !endDate) || (!startDate && endDate)) {
            return; // Don't proceed if only one date is set
        }
        if (!targetWarehouse) {
            return;
        }
        const params = {
            startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
            endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
            page,
            limit: pageSize,
            status: effectiveStatus || undefined,
            targetWarehouse: targetWarehouse || undefined,
            search: search || undefined
        };
        getInbounds(params);
    }, [page, pageSize, startDate, endDate, effectiveStatus, targetWarehouse]); // REMOVE search from deps

    // Search triggers only on Enter or icon click
    const handleSearch = () => {
        setPage(1);
        const params = {
            page: 1,
            limit: pageSize,
            startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
            endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
            status: effectiveStatus || undefined,
            targetWarehouse: targetWarehouse || undefined,
            search: search || undefined
        };
        getInbounds(params);
        setListParams({
            page: '1',
            pageSize: pageSize.toString(),
            startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
            endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
            status: effectiveStatus,
            targetWarehouse,
            search: search || undefined
        });
    };

    const handleStatusChange = (value) => {
        setPage(1);
        setStatus(value);
        setListParams({
            page: '1',
            pageSize: pageSize.toString(),
            startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
            endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
            status: value,
            targetWarehouse
        });
    };

    const handleTargetWarehouseChange = (value) => {
        setPage(1);
        setTargetWarehouse(value);
        setListParams({
            page: '1',
            pageSize: pageSize.toString(),
            startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
            endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
            status,
            targetWarehouse: value
        });
    };

    const handleDateRangeChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
        setPage(1);
        setStartDate(newStartDate);
        setEndDate(newEndDate);
        setListParams({
            page: '1',
            pageSize: pageSize.toString(),
            startDate: newStartDate ? newStartDate.format('YYYY-MM-DD') : null,
            endDate: newEndDate ? newEndDate.format('YYYY-MM-DD') : null,
            status,
            targetWarehouse
        });
    };

    const clearAllFilters = () => {
        setStatus('');
        setTargetWarehouse('');
        setStartDate(null);
        setEndDate(null);
        setPage(1);
        setListParams({
            page: '1',
            pageSize: pageSize.toString()
        });
    };

    const handleTableChange = (pagination, filters, sorter) => {
        setPage(pagination.current);
        setPageSize(pagination.pageSize);
        setListParams({
            page: pagination.current.toString(),
            pageSize: pagination.pageSize.toString(),
            startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
            endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
            status,
            targetWarehouse
        });
    };

    const handleDownloadExcel = () => {
        exportInbounds({
            startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
            endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
            search: search || undefined,
            status: effectiveStatus || undefined,
            targetWarehouse
        });
    };

    const normalizeWarehouseLabel = (warehouse) => {
        if (typeof warehouse === "string") return warehouse;
        return warehouse?.name || "";
    };

    const isOutOfChainzWarehouse = (warehouse) => {
        return normalizeWarehouseLabel(warehouse).toLowerCase() === "out of chainz";
    };

    const showOutboundReason = targetWarehouse === "Out of Chainz";
    const canDownloadReceipt = (row) => !row.isOutOfChainz && (row.status === "Partially Received" || row.status === "Received");
    const renderInboundId = (_, row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <span>{row._id}</span>
            {row.prRef ? <span>{t("inbounds.purchaseRequest")} {row.prRef}</span> : null}
        </Box>
    );
    const renderActions = (_, row) => (
        <Space>
            {canDownloadReceipt(row) && (
                <Button
                    size="small"
                    sx={{ ...main_button, textTransform: "none", py: 0.5, px: 1, fontSize: "0.75rem" }}
                    disabled={isWaiting}
                    onClick={() => downloadInboundReceipt(row._id)}
                >
                    {t("inbounds.receipt")}
                </Button>
            )}
            <Button
                size="small"
                sx={{ ...main_button, textTransform: "none", py: 0.5, px: 1, fontSize: "0.75rem" }}
                onClick={() => { setHelpTicketContext({ ticketType: "Inventory", relevantInbound: row._id }); setHelpTicketOpen(true); }}
            >
                {t("helpDesk.getHelp")}
            </Button>
        </Space>
    );

    const columns = [
        {
            title: t("common.id"),
            dataIndex: '_id',
            render: renderInboundId,
        },
        {
            title: t("inbounds.dateCreated"),
            dataIndex: 'dateCreated',
            defaultSortOrder: 'descend',
            sorter: (a, b) => { return moment(a.dateCreated).isBefore(b.dateCreated) ? -1 : 1 },
        },
        {
            title: t("common.status"),
            dataIndex: 'status',
            render: (_, { status }) => <StatusPill status={status}>{statusLabels[status] || status}</StatusPill>
        },
        {
            title: t("inbounds.totalPieces"),
            dataIndex: 'pieces',
        },
        ...(showOutboundReason ? [{
            title: t("inbounds.outboundReason"),
            dataIndex: 'notes',
            render: (notes) => (
                <Typography sx={{ maxWidth: 280, whiteSpace: 'pre-wrap' }}>
                    {notes || '-'}
                </Typography>
            ),
        }] : []),
        {
            title: t("common.actions"),
            key: 'actions',
            width: 180,
            render: renderActions,
        },
    ];

    const mobileColumns = [
        {
            title: t("common.id"),
            dataIndex: '_id',
            render: renderInboundId,
        },
        {
            title: t("common.date"),
            dataIndex: 'dateCreated',
            defaultSortOrder: 'descend',
            sorter: (a, b) => { return moment(a.dateCreated).isBefore(b.dateCreated) ? -1 : 1 },
        },
        {
            title: t("common.status"),
            dataIndex: 'status',
            render: (_, { status }) => <StatusPill status={status}>{statusLabels[status] || status}</StatusPill>
        },
        ...(showOutboundReason ? [{
            title: t("inbounds.outboundReason"),
            dataIndex: 'notes',
            render: (notes) => (
                <Typography fontSize={13} sx={{ whiteSpace: 'pre-wrap' }}>
                    {notes || '-'}
                </Typography>
            ),
        }] : []),
        {
            title: t("common.actions"),
            key: 'actions',
            render: renderActions,
        },
    ];

    const mobileExpandedColumns = [
        {
            title: t("common.sku"),
            dataIndex: 'sku',
        },
        {
            title: t("common.name"),
            dataIndex: 'name'
        },
        {
            title: t("inbounds.qtySent"),
            dataIndex: 'quantity',
        },
        {
            title: t("inbounds.qtyReceived"),
            dataIndex: 'quantityReceived',
        },
        {
            title: t("inbounds.qtyRemaining"),
            render: (_, { quantity, quantityReceived }) => parseInt(quantity) - parseInt(quantityReceived),
        },
        {
            title: t("common.actions"),
            render: (_, { _id }) => <Space>
                {(targetWarehouse === "Out of Chainz") && <Button
                    size="small"
                    sx={{ ...main_button, backgroundColor: 'var(--errorColor)', textTransform: "none", py: 0.5, px: 1, fontSize: "0.75rem" }}
                    onClick={() => cancelOutboundItem({ inboundItemId: _id })}
                >
                    {t("common.cancel")}
                </Button>}
            </Space>,
        },
    ];

    const expandedRowRender = (inbound, items, mobileColumns) => {
        const columns = mobileColumns || [
            {
                title: t("inbounds.productId"),
                dataIndex: 'inboundId',
            },
            {
                title: t("inbounds.productSku"),
                dataIndex: 'sku',
            },
            {
                title: t("common.image"),
                dataIndex: 'image',
                render: (text) => <Image width={50} height={50} src={text} preview={true} />,
            },
            {
                title: t("common.name"),
                dataIndex: 'name'
            },
            {
                title: t("common.size"),
                dataIndex: 'size',
            },
            {
                title: t("common.color"),
                dataIndex: 'color',
            },
            {
                title: t("inbounds.weight"),
                dataIndex: 'weight',
            },
            {
                title: t("inbounds.qtySent"),
                dataIndex: 'quantity',
            },
            {
                title: t("inbounds.qtyReceived"),
                dataIndex: 'quantityReceived',
            },
            {
                title: t("inbounds.qtyRemaining"),
                render: (_, { quantity, quantityReceived }) => parseInt(quantity) - parseInt(quantityReceived),
            },
            {
                title: t("common.actions"),
                render: (_, { _id }) => <Space>
                    {(inbound.isOutOfChainz || targetWarehouse === "Out of Chainz") && <Button
                        size="small"
                        sx={{ ...main_button, backgroundColor: 'var(--errorColor)', textTransform: "none", py: 0.5, px: 1, fontSize: "0.75rem" }}
                        onClick={() => cancelOutboundItem({ inboundItemId: _id })}
                    >
                        {t("common.cancel")}
                    </Button>}
                </Space>,
            },
        ];

        const data = items?.map((row) => {
            return {
                _id: row._id,
                inboundId: row._id,
                sku: row.product.sku,
                image: row.product.image,
                name: row.product.name,
                size: row.product.size,
                color: row.product.color,
                weight: row.product.weight,
                quantity: row.quantity,
                quantityReceived: row.quantityReceived,
            }
        })
        return <Table columns={columns} dataSource={data} pagination={false} />;
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="cz-wrap" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {warehouseTabList.map((w, idx) => (
                    <button
                        key={w.value}
                        type="button"
                        className={`cz-chip ${warehouseTab === idx ? 'on' : ''}`}
                        onClick={() => handleWarehouseTabChange(null, idx)}
                    >
                        {w.label}
                    </button>
                ))}
            </div>
            <FilterChips
                value={effectiveStatus}
                onChange={handleStatusChange}
                options={[
                    ...(!isReturnsWarehouseSelected ? [['', t("orders.allStatuses")]] : []),
                    ...statusFilterOptions.map((option) => [option, statusLabels[option] || option]),
                ]}
            />
            <SearchFilterBar
                placeholder={t("inbounds.searchPlaceholder")}
                value={search}
                onChange={setSearch}
                onSearch={handleSearch}
                isMobile={isMobile}
                filterOpen={showFilters}
                onToggleFilter={() => setShowFilters(!showFilters)}
                filterLabel={t("common.filter")}
                actions={(
                    <button type="button" className="cz-chip" onClick={handleDownloadExcel} disabled={isWaiting}>
                        {t("common.export")}
                        {exportIcon}
                    </button>
                )}
            >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography sx={{ color: 'var(--tx2)' }}>{t("dateRange.selectDateRange")}</Typography>
                            <DateRangePicker
                                startDate={startDate}
                                endDate={endDate}
                                onChange={handleDateRangeChange}
                                fullWidth
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} sx={{ ...centered_flex_box }}>
                            <Button sx={{ ...main_button }} fullWidth onClick={() => clearAllFilters()}>{t("common.clearAll")}</Button>
                        </Grid>
                    </Grid>
                </Box>
            </SearchFilterBar>
            <Table
                columns={isMobile ? mobileColumns : columns}
                dataSource={inbounds?.docs?.map((row) => ({
                    _id: row._id,
                    prRef: row.prRef,
                    dateCreated: moment(row.createdAt).format("lll"),
                    targetWarehouse: normalizeWarehouseLabel(row.targetWarehouse),
                    isOutOfChainz: isOutOfChainzWarehouse(row.targetWarehouse),
                    status: row.status,
                    notes: row.notes,
                    items: row.items,
                    pieces: row.items.reduce((a, b) => a + b.quantity, 0)
                }))}
                loading={isLoading}
                rowKey={"_id"}
                expandable={{
                    expandedRowRender: (record) => expandedRowRender(record, record.items, isMobile ? mobileExpandedColumns : undefined)
                }}
                showSorterTooltip={{
                    target: 'sorter-icon',
                }}
                pagination={{
                    current: page,
                    pageSize: pageSize,
                    total: inbounds?.totalDocs,
                    showSizeChanger: false,
                    showQuickJumper: false,
                    showTotal: (total) => `${t('table.total')} ${total} ${t('table.items')}`,
                }}
                onChange={handleTableChange}
                scroll={{ x: isMobile ? 300 : undefined }}
            />
            <CreateTicketModal
                open={helpTicketOpen}
                onClose={() => { setHelpTicketOpen(false); setHelpTicketContext(null); }}
                token={token}
                context={helpTicketContext}
                onSuccess={() => setHelpTicketOpen(false)}
            />
        </div>
    );
};

const mapStateToProps = (state) => ({
    user: state?.auth?.user,
    isLoading: state?.records?.isLoading,
    isWaiting: state?.wait?.isLoading,
    token: state?.auth?.token,
    inbounds: state?.records?.inbounds,
    warehouses: state?.records?.warehouses
});

const mapDispatchToProps = { getInbounds, cancelOutboundItem, getWarehouses, exportInbounds, downloadInboundReceipt };

export default connect(mapStateToProps, mapDispatchToProps)(Inbounds);
